import React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Carousel, CarouselContent, CarouselDots, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "ui/Carousel";
import VideoPlayer from "components/Common/VideoPlayer";

export interface MediaManagerItem {
  preview: string;
  type: "image" | "video";
  order: number;
}

export interface MediaManagerCarouselProps {
  files: MediaManagerItem[];
  aspectClass: string;
  onMove: (index: number, dir: "left" | "right") => void;
  onRemove: (index: number) => void;
  className?: string;
  setApi?: (api: CarouselApi) => void;
}

const MediaManagerCarousel: React.FC<MediaManagerCarouselProps> = ({ files, aspectClass, onMove, onRemove, className, setApi }) => {
  if (!files || files.length === 0) return null;

  const renderItem = (m: MediaManagerItem, idx: number) => (
    <div className={`relative border rounded-lg overflow-hidden bg-transparent ${aspectClass} flex items-center justify-center`}>
      {m.type === "image" ? (
        <img src={m.preview} alt={`Media ${idx + 1}`} className="max-w-full max-h-[calc(100vh-240px)] object-contain" />
      ) : (
        <VideoPlayer url={m.preview} muted controls objectFit="contain" maxHeight="calc(100vh-240px)" className="w-full h-full" />
      )}
      <div className="absolute top-2 right-2 flex items-center gap-1">
        <button type="button" onClick={() => onMove(idx, "left")} disabled={idx === 0} className="p-1 bg-white bg-opacity-80 hover:bg-opacity-90 text-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Move left">
          <ChevronLeft className="size-3" />
        </button>
        <div className="bg-white bg-opacity-80 text-black rounded-full px-2 py-1 text-xs">{idx + 1}/{files.length}</div>
        <button type="button" onClick={() => onMove(idx, "right")} disabled={idx === files.length - 1} className="p-1 bg-white bg-opacity-80 hover:bg-opacity-90 text-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Move right">
          <ChevronRight className="size-3" />
        </button>
      </div>
      <button type="button" onClick={() => onRemove(idx)} className="absolute top-2 left-2 size-7 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover" aria-label="Remove media">
        <span className="flex items-center justify-center"><X className="size-4 stroke-1" /></span>
      </button>
    </div>
  );

  if (files.length === 1) {
    return (
      <div className={className}>
        {renderItem(files[0], 0)}
      </div>
    );
  }

  return (
    <div className={className}>
      <Carousel className="w-full" setApi={setApi}
      >
        <CarouselContent>
          {files.map((m, idx) => (
            <CarouselItem key={m.preview} className="md:basis-1/2 lg:basis-1/3">
              {renderItem(m, idx)}
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious transparent className="absolute left-2 top-1/2 -translate-y-1/2" />
        <CarouselNext transparent className="absolute right-2 top-1/2 -translate-y-1/2" />
        <CarouselDots />
      </Carousel>
    </div>
  );
};

export default MediaManagerCarousel;


